import { observeSize } from "@utils/observeSize"
import { cn } from "@utils/tw"
import { motion, springValue, transformValue, useMotionValue } from "motion/react"
import { Mesh, Program, Renderer, Triangle, Vec2, Vec3 } from "ogl"
import { ComponentPropsWithoutRef, useEffect, useRef, useState } from "react"
import { hsl } from "./generator/lib/hsl"
import { simplex3d } from "./generator/lib/simplex3d"
import { ClientOnly, useHydrating } from "eddev/routing"

type Props = ComponentPropsWithoutRef<"div"> & {
  /** Function to return parameters for the animation. Spring/lerp is handled automatically */
  getParams: (args: { hovering: boolean; mobile: boolean; inView: boolean }) => Params
  /** Whether the WebGL context should include transparency (if false, solid black is used) */
  alpha: boolean
}

type Params = {
  opacity: number
  feather: number
}

/**
 * Renders a glowing box using WebGL. It's rendered as `absolute inset-0 z-[-1]`
 *
 * Pass in `getParams` to return the parameters based on interactive state. Lerping between values is handled automatically.
 *
 */
export function GlowingBox({ getParams, alpha, ...props }: Props) {
  const ref = useRef<HTMLDivElement>(null!)
  const hydrating = useHydrating()
  const [hovering, setHovering] = useState(false)
  const [inView, setInView] = useState(false)
  const enabledRef = useRef(false)
  enabledRef.current = inView

  const motionParams = useMotionValue<Params>({
    opacity: 0,
    feather: 0,
  })

  const motionMouse = useMotionValue({ x: 0.5, y: 0.5, alpha: 0 })

  useEffect(() => {
    const params = getParams({ hovering, mobile: false, inView })
    motionParams.set(params)
  })

  useEffect(() => {
    if (hydrating) return
    const target = ref.current

    const renderer = new Renderer({ dpr: 1, alpha: alpha })
    const gl = renderer.gl
    target.appendChild(renderer.gl.canvas)
    gl.clearColor(0, 0, 0, 0)

    const geometry = new Triangle(gl)
    const program = new Program(gl, {
      vertex,
      fragment,
      uniforms: {
        uTime: { value: Math.random() * 1000.0 },
        uAlpha: { value: 0 },
        uMouse: { value: new Vec3(0.5, 0.5, 0) },
        uRes: { value: new Vec2(1, 1) },
      },
    })
    const mesh = new Mesh(gl, { geometry, program })

    let raf = 0

    const opacitySpring = springValue(
      transformValue(() => motionParams.get().opacity),
      { damping: 20, stiffness: 100 },
    )
    const mouseSpring = {
      x: springValue(
        transformValue(() => motionMouse.get().x),
        { damping: 10, stiffness: 60 },
      ),
      y: springValue(
        transformValue(() => motionMouse.get().y),
        { damping: 10, stiffness: 60 },
      ),
      alpha: springValue(transformValue(() => motionMouse.get().alpha)),
    }

    function draw() {
      if (enabledRef.current) {
        renderer.render({ scene: mesh })
      }
    }

    function frame(t: number) {
      program.uniforms.uTime.value = t * 0.001
      program.uniforms.uAlpha.value = opacitySpring.get()
      program.uniforms.uRes.value.set(renderer.width, renderer.height)
      program.uniforms.uMouse.value.set(mouseSpring.x.get(), mouseSpring.y.get(), mouseSpring.alpha.get())
      if (opacitySpring.get() !== 0) {
        draw()
      }
      raf = requestAnimationFrame(frame)
    }

    const disposeSizer = observeSize(gl.canvas, ({ width, height }) => {
      renderer.setSize(width, height)
      draw()
    })

    raf = requestAnimationFrame(frame)

    return () => {
      target.removeChild(renderer.gl.canvas)
      loseContext(gl)
      disposeSizer()
      cancelAnimationFrame(raf)
    }
  }, [hydrating])

  return (
    <motion.div
      className={cn("relative isolate", props.className)}
      onMouseEnter={(e) => {
        setHovering(true)
        props.onMouseEnter?.(e)
      }}
      onMouseLeave={(e) => {
        setHovering(false)
        motionMouse.set({
          ...motionMouse.get(),
          alpha: 0,
        })
        props.onMouseLeave?.(e)
      }}
      onViewportEnter={() => setInView(true)}
      onViewportLeave={() => setInView(false)}
      onPointerMove={(e) => {
        const bounds = ref.current.getBoundingClientRect()
        motionMouse.set({
          x: (e.clientX - bounds.left) / bounds.width,
          y: 1 - (e.clientY - bounds.top) / bounds.height,
          alpha: 1,
        })
      }}
      onTouchEnd={() => {
        motionMouse.set({
          ...motionMouse.get(),
          alpha: 0,
        })
      }}
    >
      <ClientOnly>
        <div ref={ref} className="z-[-1] absolute inset-0 [&_canvas]:!size-full"></div>
      </ClientOnly>
      {props.children}
    </motion.div>
  )
}

function loseContext(gl: WebGLRenderingContext) {
  const ext = gl.getExtension("WEBGL_lose_context")
  ext?.loseContext()
}

const vertex = /* glsl */ `
  attribute vec2 uv;
  attribute vec2 position;

  varying vec2 vUv;

  void main() {
    vUv = uv;
    gl_Position = vec4(position, 0, 1);
  }
`

const fragment = /* glsl */ `
  precision highp float;

  uniform float uTime;
  uniform float uAlpha;
  uniform vec3 uMouse;
  uniform vec2 uRes;
  varying vec2 vUv;

  ${simplex3d}
  ${hsl}

  vec2 rotUv() {
    vec2 tuv = vUv - 0.5;

    // rotation by 45 degrees
    float angle = 0.785398; // radians (45 degrees)
    float s = sin(angle);
    float c = cos(angle);

    tuv = vec2(
      tuv.x * c - tuv.y * s,
      tuv.x * s + tuv.y * c
    );

    // re-center
    tuv += 0.5;
    return tuv;
  }

  void main() {
    float mouseDist = length((uMouse.xy - vUv) * (uRes)) / ((uRes.x + uRes.y) / 2.0);
    float mouseEffect = max(pow(1.0 - mouseDist, 2.0), 0.01) * uMouse.z;

    vec2 tuv = rotUv();

    vec2 pixelSize = 1.0 / vec2(uRes.x, uRes.y) * 5.0;
    float pixel = length((floor(tuv / pixelSize) * pixelSize) - (tuv + pixelSize/2.0));

    float h = 0.74 + snoise(vec3(tuv * 0.5, uTime * 0.3 + 5.0 - pixel * 1.0)) * 0.05;
    float s = 0.8 - mouseEffect * 0.1;
    float l = 0.2 + snoise(vec3(tuv * 0.9, uTime * 0.4 + 12.0)) * 0.1 - pixel * 0.6 + mouseEffect * 0.15;

    vec3 color = hsl2rgb(h, s, l);

    // color = vec3(pixel);
    gl_FragColor.rgb = color * uAlpha;
    gl_FragColor.a = uAlpha;
  }
`
